import { defineStore } from 'pinia'
import { db } from 'src/models/db'
import type { UserProfile, UserSettings } from './auth-store-types'

// 1. EXTRACT DEFAULTS TO KEEP IT DRY
const DEFAULT_USER_SETTINGS: UserSettings = {
  defaultInnings: 6,
  defaultRosterSize: 12,
  themePreference: 'system',
  enablePitchQuickLog: true,
  enablePitchCategories: true,
  enablePitchTypes: true,
  enablePitchQuality: true,
  usePitcherPerspective: false,
  useReadablePlayStatus: false,
  autoAdvanceLeadRunner: true,
  useDesignatedHitter: false,
  hiddenPitchOptions: [],
  hiddenApproachOptions: [],
  hiddenExecutionOptions: [],
  hiddenTransferOptions: [],
  hiddenThrowOptions: [],
  hiddenDecisionOptions: [],
  hiddenQualityOptions: [],
  hiddenDimensions: [],
  hiddenRunnerOptions: [],
  hiddenScoutingTags: [],
  helpModeEnabled: true,
  customLabels: {},
  customCategories: {},
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    currentUser: null as UserProfile | null,
    isInitialized: false,
    lastActiveUserId: localStorage.getItem('pitchTracker_activeUserId') || null,
  }),
  getters: {
    IsUserLoggedIn: (state) => state.currentUser !== null,
    CurrentUserName: (state) => (state.currentUser ? state.currentUser.username : ''),
  },

  actions: {
    // 1. App Startup: Check if a user was already logged in
    async InitAuth() {
      if (this.lastActiveUserId) {
        const user = await db.users.get(this.lastActiveUserId)
        if (user) {
          this.currentUser = user
        }
      }
      this.isInitialized = true
    },

    // 2. Create a new local profile
    async RegisterLocalUser(username: string) {
      const newUser: UserProfile = {
        id: crypto.randomUUID(),
        username,
        settings: JSON.parse(JSON.stringify(DEFAULT_USER_SETTINGS)), // Use a deep copy
        createdAt: new Date().toISOString(),
      }

      await db.users.add(newUser)
      this.currentUser = newUser
      this.lastActiveUserId = newUser.id
      localStorage.setItem('pitchTracker_activeUserId', newUser.id)
    },

    // 3. Log into a profile
    async Login(username: string) {
      // FIX: Query Dexie by the username index, not the primary key
      // Note: Ensure 'username' is indexed in your Dexie schema (e.g., 'id, username')
      const user = await db.users.where('username').equalsIgnoreCase(username).first()

      if (user) {
        this.currentUser = user
        this.lastActiveUserId = user.id
        localStorage.setItem('pitchTracker_activeUserId', user.id)
      } else {
        await this.RegisterLocalUser(username)
      }
    },
    // --- NEW: IN-MEMORY GUEST USER ---
    loadGuestProfile() {
      // Only load guest if there isn't already a real user logged in
      if (!this.currentUser) {
        this.currentUser = {
          id: 'temp-guest-id', // Special ID we can check against later
          username: 'Guest Observer',
          settings: JSON.parse(JSON.stringify(DEFAULT_USER_SETTINGS)),
          createdAt: new Date().toISOString()
        }
      }
    },

    // 4. Update User Settings
    async updateSettings(newSettings: Partial<UserSettings>) {
      if (!this.currentUser) return

      // Merge the existing settings with the incoming changes
      this.currentUser.settings = {
        ...this.currentUser.settings,
        ...newSettings,
      }

      // Persist to Dexie
      try {
        await db.users.update(this.currentUser.id, {
          settings: JSON.parse(JSON.stringify(this.currentUser.settings)),
        })
      } catch (err) {
        console.error('Failed to save settings to offline DB:', err)
      }
    },

    // 4. Log out
    logout() {
      this.currentUser = null
      localStorage.removeItem('pitchTracker_activeUserId')
      // localStorage.removeItem('pitchTracker_activeUserId')
    },
  },
})
